import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, User, Palette } from 'lucide-react';

export default function DanceFloorsOnePerson() {
  const navigate = useNavigate();
  const [colorScheme, setColorScheme] = useState('standard'); // 'standard' or 'steve'

  // Color schemes
  const colors = colorScheme === 'standard' 
    ? {
        observation: { bg: 'bg-blue-600', text: 'text-white', label: 'Observation (Blue)' },
        feeling: { bg: 'bg-red-600', text: 'text-white', label: 'Feeling (Red)' },
        need: { bg: 'bg-green-600', text: 'text-white', label: 'Need (Green)' },
        request: { bg: 'bg-amber-500', text: 'text-white', label: 'Request (Gold)' }
      }
    : {
        observation: { bg: 'bg-amber-500', text: 'text-white', label: 'Observation (Gold)' },
        feeling: { bg: 'bg-red-600', text: 'text-white', label: 'Feeling (Red)' },
        need: { bg: 'bg-blue-600', text: 'text-white', label: 'Need (Blue)' },
        request: { bg: 'bg-green-600', text: 'text-white', label: 'Request (Green)' }
      };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">NVC Dance Floors (One Person)</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setColorScheme(colorScheme === 'standard' ? 'steve' : 'standard')}
                variant="outline"
                size="sm"
                className="border-purple-600 hover:border-purple-500 text-white hover:bg-purple-900/20"
              >
                <Palette className="h-4 w-4 mr-2" />
                {colorScheme === 'standard' ? "Steve's Colors" : 'Standard Colors'}
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 11</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-8">
            <User className="h-16 w-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-3">NVC Dance Floors Communication Process</h2>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              A visual map showing the flow between self-empathy, honesty, and empathy. 
              The arrow shows how we start with self-connection and move to expression or empathy.
            </p>
          </div>

          {/* Color Scheme Legend */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 mb-8 flex items-center justify-center gap-6">
            <span className="text-slate-300 font-semibold">Color Key:</span>
            <div className={`${colors.observation.bg} ${colors.observation.text} px-3 py-1 rounded text-sm font-semibold`}>{colors.observation.label}</div>
            <div className={`${colors.feeling.bg} ${colors.feeling.text} px-3 py-1 rounded text-sm font-semibold`}>{colors.feeling.label}</div>
            <div className={`${colors.need.bg} ${colors.need.text} px-3 py-1 rounded text-sm font-semibold`}>{colors.need.label}</div>
            <div className={`${colors.request.bg} ${colors.request.text} px-3 py-1 rounded text-sm font-semibold`}>{colors.request.label}</div>
          </div>

          {/* Dance Floors Layout */}
          <div className="relative">
            {/* Top Row: Empathy (Left) and Honesty (Right) */}
            <div className="grid grid-cols-2 gap-8 mb-16">
              {/* EMPATHY Box (LEFT) */}
              <div className="bg-black border-4 border-cyan-600 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-cyan-400 mb-1 text-center">EMPATHY</h3>
                <p className="text-cyan-300 text-center text-xs mb-6 italic">Conscious intention to connect with another</p>
                
                {/* Stacked from bottom to top */}
                <div className="space-y-3">
                  {/* Request (TOP) */}
                  <div className={`${colors.request.bg} rounded-lg p-4`}>
                    <p className={`${colors.request.text} font-bold mb-2`}>WOULD YOU LIKE:</p>
                    <ul className={`${colors.request.text} text-sm space-y-1 list-disc list-inside`}>
                      <li>me to tell you what I heard?</li>
                      <li>me to tell you what's going on for me?</li>
                      <li>me to [do]...? (concrete action)</li>
                    </ul>
                  </div>

                  {/* Need */}
                  <div className={`${colors.need.bg} rounded-lg p-4`}>
                    <p className={`${colors.need.text} font-bold mb-1`}>YOU NEED</p>
                    <p className={`${colors.need.text} text-sm`}>because (value)</p>
                  </div>

                  {/* Feeling */}
                  <div className={`${colors.feeling.bg} rounded-lg p-4`}>
                    <p className={`${colors.feeling.text} font-bold`}>do you FEEL</p>
                  </div>

                  {/* Observation (BOTTOM) */}
                  <div className={`${colors.observation.bg} rounded-lg p-4`}>
                    <p className={`${colors.observation.text} font-bold mb-1`}>WHEN YOU</p>
                    <p className={`${colors.observation.text} text-sm`}>Hear, See, Taste, Touch, Smell</p>
                    <p className={`${colors.observation.text} text-xs mt-1`}>OR REMEMBER</p>
                  </div>
                </div>
              </div>

              {/* HONESTY Box (RIGHT) */}
              <div className="bg-black border-4 border-purple-600 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-purple-400 mb-1 text-center">HONESTY</h3>
                <p className="text-purple-300 text-center text-xs mb-6 italic">Conscious intention to connect with another</p>
                
                {/* Stacked from bottom to top */}
                <div className="space-y-3">
                  {/* Request (TOP) */}
                  <div className={`${colors.request.bg} rounded-lg p-4`}>
                    <p className={`${colors.request.text} font-bold mb-2`}>WOULD YOU BE WILLING TO:</p>
                    <ul className={`${colors.request.text} text-sm space-y-1 list-disc list-inside`}>
                      <li>tell me what you heard me say?</li>
                      <li>tell me what's going on for you?</li>
                      <li>[do]...? (concrete action)</li>
                    </ul>
                  </div>

                  {/* Need */}
                  <div className={`${colors.need.bg} rounded-lg p-4`}>
                    <p className={`${colors.need.text} font-bold mb-1`}>I NEED</p>
                    <p className={`${colors.need.text} text-sm`}>because (value)</p>
                  </div>

                  {/* Feeling */}
                  <div className={`${colors.feeling.bg} rounded-lg p-4`}>
                    <p className={`${colors.feeling.text} font-bold`}>I FEEL</p>
                  </div>

                  {/* Observation (BOTTOM) */}
                  <div className={`${colors.observation.bg} rounded-lg p-4`}>
                    <p className={`${colors.observation.text} font-bold mb-1`}>WHEN I</p>
                    <p className={`${colors.observation.text} text-sm`}>Hear, See, Taste, Touch, Smell</p>
                    <p className={`${colors.observation.text} text-xs mt-1`}>OR REMEMBER</p>
                  </div>
                </div>
              </div>
            </div>

            {/* SELF-EMPATHY Box (CENTERED BELOW) */}
            <div className="relative">
              {/* Upward Arrow */}
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <div className="text-6xl text-green-400">↑</div>
                <p className="text-green-300 text-sm font-semibold mt-2">Flow from Self-Connection</p>
              </div>

              <div className="max-w-md mx-auto bg-black border-4 border-green-600 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-green-400 mb-1 text-center">SELF-EMPATHY</h3>
                <p className="text-green-300 text-center text-xs mb-6 italic">Conscious intention to connect with self</p>
                
                {/* Stacked from bottom to top */}
                <div className="space-y-3">
                  {/* Request (TOP) */}
                  <div className={`${colors.request.bg} rounded-lg p-4`}>
                    <p className={`${colors.request.text} font-bold mb-1`}>REQUEST</p>
                    <p className={`${colors.request.text} text-sm`}>What might be my present request for connection or action?</p>
                  </div>

                  {/* Need */}
                  <div className={`${colors.need.bg} rounded-lg p-4`}>
                    <p className={`${colors.need.text} font-bold mb-1`}>NEED</p>
                    <p className={`${colors.need.text} text-sm`}>What do I need now?</p>
                  </div>

                  {/* Feeling */}
                  <div className={`${colors.feeling.bg} rounded-lg p-4`}>
                    <p className={`${colors.feeling.text} font-bold mb-1`}>FEELING</p>
                    <p className={`${colors.feeling.text} text-sm`}>How am I feeling now about it?</p>
                  </div>

                  {/* Observation (BOTTOM) */}
                  <div className={`${colors.observation.bg} rounded-lg p-4`}>
                    <p className={`${colors.observation.text} font-bold mb-1`}>OBSERVATION</p>
                    <p className={`${colors.observation.text} text-sm`}>What happened? (facts only)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The Four D's Warning */}
          <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-6 mt-12 mb-8">
            <h3 className="text-2xl font-bold text-red-200 mb-4">⚠️ The Four D's of DISCONNECTION</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-red-900/30 rounded px-4 py-3 border border-red-700/30">
                <p className="text-red-100 font-semibold">Diagnosing or Judging</p>
              </div>
              <div className="bg-red-900/30 rounded px-4 py-3 border border-red-700/30">
                <p className="text-red-100 font-semibold">Denying responsibility</p>
              </div>
              <div className="bg-red-900/30 rounded px-4 py-3 border border-red-700/30">
                <p className="text-red-100 font-semibold">Deserve-type thinking</p>
              </div>
              <div className="bg-red-900/30 rounded px-4 py-3 border border-red-700/30">
                <p className="text-red-100 font-semibold">Demanding</p>
              </div>
            </div>
          </div>

          {/* Teaching Notes */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8">
            <h3 className="text-xl font-bold text-white mb-4">Teaching Notes</h3>
            <div className="space-y-3 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold">Start with Self-Empathy (Green Box):</span> Before expressing or empathizing, 
                connect with what's happening inside you. Notice what you observe, feel, and need.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Move to Empathy (Left, Cyan) or Honesty (Right, Purple):</span> From self-connection, 
                you can either empathize with the other person's experience or express your own honestly.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">The Upward Flow:</span> The arrow shows how self-empathy is the foundation. 
                We start by connecting with ourselves (bottom), then move upward to either empathy or honesty.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Components Flow Bottom-to-Top:</span> Within each box, start with observation 
                (what happened), move to feeling (emotional response), then need (what's important), and finally request 
                (what would help).
              </p>
              <p className="leading-relaxed mt-4 text-slate-300 italic">
                Think of it as a dance—you move fluidly between these three spaces. The color scheme helps track 
                which component you're working with across all three areas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
